import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { logClientError } from "@/lib/error-logger";

interface Props {
  children: ReactNode;
  /** Identificador da fronteira para rastreio (ex.: "Discover", "App"). */
  source?: string;
  /** Função opcional que devolve contexto adicional no momento do erro. */
  getContext?: () => Record<string, unknown>;
  /** Renderização customizada da UI de erro (recebe error e função de reset). */
  fallback?: (params: { error?: Error; reset: () => void }) => ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const source = this.props.source ?? "ErrorBoundary";
    let extraContext: Record<string, unknown> = {};
    try {
      extraContext = this.props.getContext?.() ?? {};
    } catch (ctxErr) {
      extraContext = { contextError: (ctxErr as Error)?.message };
    }

    const route = typeof window !== "undefined" ? window.location?.pathname : null;
    const url = typeof window !== "undefined" ? window.location?.href : null;

    console.error(
      `[ErrorBoundary:${source}] route=${route} message="${error.message}"`,
      { error, errorInfo, context: extraContext }
    );

    // Reporta para client_error_logs (não bloqueante)
    void logClientError({
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack ?? undefined,
      severity: "fatal",
      metadata: {
        source,
        route,
        url,
        errorName: error.name,
        retryCount: this.state.retryCount,
        timestamp: new Date().toISOString(),
        ...extraContext,
      },
    });

    // Auto-retry on dynamic import failures (Vite HMR issue)
    const isDynamicImportError =
      error.message?.includes("Failed to fetch dynamically imported module") ||
      error.message?.includes("Importing a module script failed");

    if (isDynamicImportError && this.state.retryCount < 3) {
      this.setState(
        (prev) => ({ retryCount: prev.retryCount + 1, hasError: false, error: undefined }),
        () => {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      );
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: undefined, retryCount: 0 });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback({ error: this.state.error, reset: this.reset });
      }
      return (
        <div
          role="alert"
          aria-live="assertive"
          className="min-h-dvh bg-background flex items-center justify-center px-6"
        >
          <div className="text-center max-w-sm">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" aria-hidden="true" />
            <h2 className="text-xl font-display font-bold text-foreground mb-2">
              Algo deu errado
            </h2>
            <p className="text-muted-foreground font-body text-sm mb-6">
              Ocorreu um erro inesperado. Tente recarregar a página.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="gradient-warm px-6 py-2.5 rounded-full font-body font-medium text-primary-foreground text-sm hover:scale-105 transition-transform inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              Recarregar
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
